import { apiUriPrefixProd } from "~/shared/api"

import type { IUserInfo } from "../types"

/**
 * Исключение, которое выбрасывается при ошибке аутентификации
 */
export class AuthError extends Error {
	/** http-код */
	status: string

	constructor(status, message) {
		super(message)
		this.name = "AuthError"
		this.status = status
		this.message = message
	}
}

/** Тип аутентификации */
export enum AuthenticateType {
	/** Обычная аутентификация */
	DEFAULT,
	/** Двухфакторная аутентификация, которая требует дополнительно ввести код */
	TWO_FACTOR,
}

export enum AuthenticateResultType {
	IN_PROGRESS,
	SUCCESS,
	TWO_FACTOR,
	ERROR,
}

/**
 * Представляет успешный результат {@link authenticate}
 */
export interface SuccessAuthenticateResult {
	type: AuthenticateResultType.SUCCESS
	/** токен доступа */
	accessToken: string
	/** инф-я о пользователе */
	userInfo: IUserInfo
	/** refresh-токен */
	refreshToken: string
}

/** Представляет успешный результат, но который требует подтверждение кодом (2FA) */
export interface TwoFactorAuthenticateResult {
	type: AuthenticateResultType.TWO_FACTOR
}

/**
 * Аутентификация
 * @throws {AuthError}
 * @param login логин
 * @param password пароль
 * @param accessTokenLifetime время жизни токена
 * @param authType
 * @returns при успешной аутентификации, вернет токен и информацию о пользователе
 */
export async function authenticate(
	login: string,
	password: string,
	accessTokenLifetime?: string,
	authType: AuthenticateType = AuthenticateType.DEFAULT,
) {
	const response = await fetch(
		`${apiUriPrefixProd}/security/${authType === AuthenticateType.DEFAULT ? "auth" : "auth-by-code"}`,
		{
			method: "post",
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				login: login,
				[authType === AuthenticateType.DEFAULT ? "password" : "code"]:
					password,
				lifetime: accessTokenLifetime,
			}),
		},
	)

	const result = await response.json()

	if (!response.ok) {
		throw new AuthError(response.status, result.message ?? result.error)
	}

	if (authType === AuthenticateType.DEFAULT && result.twoFactorAuthEnabled) {
		return {
			type: AuthenticateResultType.TWO_FACTOR,
		} as TwoFactorAuthenticateResult
	}

	return {
		type: AuthenticateResultType.SUCCESS,
		accessToken: result.accessToken,
		refreshToken: result.refreshToken,
		userInfo: result.userInfo,
	} as SuccessAuthenticateResult
}
